import { isCoarsePointer } from '@/lib/platform';
/**
 * Focus and keyboard rules for touch-first devices (phones, iPads, Surface in
 * tablet mode).
 *
 * Focusing a text field on those devices raises the on-screen keyboard, which
 * covers half a phone and (under 16px text) makes iOS zoom the page. Dialogs
 * and sheets that open on a tap must therefore not drop focus into a field on
 * their own; a keyboard/mouse user on the same form keeps the first-field focus.
 */
export { isCoarsePointer };
/**
 * `onOpenAutoFocus` for a Radix Dialog, Sheet or Popover: on a coarse pointer,
 * focus the content container instead of its first tabbable element. Focus
 * (and the screen reader) stays inside the overlay; no keyboard pops up over a
 * panel the reader has not seen yet, and no first keystroke overwrites a
 * prefilled field. The listener sits on the content node, which Radix renders
 * focusable (tabIndex -1); focusing it does not scroll the page.
 */
export function focusContainerOnTouch(event) {
    if (!isCoarsePointer())
        return;
    event.preventDefault();
    const container = event.currentTarget;
    if (container instanceof HTMLElement)
        container.focus({ preventScroll: true });
}
/**
 * Keyboard hints for identifier fields (patient ID, accession, AE title, codes):
 * the iPhone/iPad keyboard otherwise capitalises the first letter and, on space
 * or return, "corrects" unusual values into dictionary words.
 */
export const IDENTIFIER_FIELD_PROPS = {
    autoCapitalize: 'off',
    autoCorrect: 'off',
    spellCheck: false,
};
/**
 * Search boxes: the identifier hints (a surname search must not be rewritten
 * either) plus a Search key on the on-screen keyboard.
 */
export const SEARCH_FIELD_PROPS = {
    ...IDENTIFIER_FIELD_PROPS,
    enterKeyHint: 'search',
};
/**
 * Name fields (patient, owner, vet, practice): capitalise each word, but never
 * "correct" a surname into a dictionary word.
 */
export const NAME_FIELD_PROPS = {
    autoCapitalize: 'words',
    autoCorrect: 'off',
    spellCheck: false,
};
